import _ from "lodash";


export const cx = (...classes: any): string => {
  return classes.filter(Boolean).join(" ");
};

export const fmtDate = (date: Date, opts?: Intl.DateTimeFormatOptions) => {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    year: "numeric",
    month: "long",
    day: "numeric",
    ...opts
  }).format(date);
};

export function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

export const fmtCurrency = (amount?: number, currencyCode: string = "USD", minimumFractionDigits: number = 0) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits,
  }).format(amount || 0);
};

// For comparison/reference prices (e.g. struck-out "$49 with monthly billing"):
// whole values render with no decimals, fractional values with two.
export const fmtCurrencyCompact = (amount?: number, currencyCode: string = "USD") => {
  const value = amount || 0;
  return fmtCurrency(value, currencyCode, Number.isInteger(value) ? 0 : 2);
};

export const fmtNumber = (amount?: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 0
  }).format(amount || 0);
};

export function fmtShortNumber(amount: number) {
  if (amount >= 1_000_000) {
    if (amount % 1_000_000 > 0) {
      return (amount / 1_000_000).toFixed(1) + "M";
    } else {
      return (amount / 1_000_000) + "M";
    }
  }
  if (amount >= 1_000) {
    if (amount % 1_000 > 0) {
      return (amount / 1_000).toFixed(1) + "K";
    } else {
      return (amount / 1_000) + "K";
    }
  }
  return amount.toString(10);
}

export const sleep = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));

export const join = (arr: Array<any>, separator: string, lastSeparator?: string) => {
  if (lastSeparator && (arr.length > 1)) {
    return `${arr.slice(0, -1).join(separator)}${lastSeparator}${_.last(arr)}`;
  } else {
    return `${arr.join(separator)}`;
  }
};

export const toNumber = (value: string | number | undefined | null): number => {
  return typeof value === "number" ? value : Number(value) || 0;
};

export const snakeCaseToCamelCase = (data: any) => _.mapKeys(data, (_value, key) => _.camelCase(key));

export const removeNonMatchingProperties = (firstItem: any, secondItem: any) => {
  for (const key in firstItem) {
    if (!secondItem.hasOwnProperty(key)) {
      delete firstItem[key];  // Delete the property if it doesn't exist in the secondItem
    }
  }
}

export const convertStringsInObject = (obj: any) => {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];

      // Convert 'true' or 'false' strings to boolean
      if (typeof value === 'string' && (value === 'true' || value === 'false')) {
        obj[key] = value === 'true';
      }

      // Convert latitude and longitude to numbers
      if ((key === 'latitude' || key === 'longitude') && !isNaN(parseFloat(value))) {
        obj[key] = parseFloat(value);
      }
    }
  }
  return obj;
};

export const formatCredits = (num: number) => {
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}k`;
  }
  return num.toString();
};

export const formatCreditsWithCommaSeparators = (credits: number | undefined | null): string => {
  if (credits === null || credits === undefined) {
    return '-';
  }
  let creditsLeft = credits;
  let pieces = [];
  while (creditsLeft >= 1000) {
    const remainder = creditsLeft % 1000;
    pieces.unshift(remainder.toString().padStart(3, '0'));
    creditsLeft = Math.floor(creditsLeft / 1000);
  }
  pieces.unshift(creditsLeft.toString());
  return pieces.join(',');
};